/* eslint-disable func-names */
/* eslint-disable no-param-reassign */
import _clone from 'lodash/clone'
import _range from 'lodash/range'
import _last from 'lodash/last'
import _now from 'lodash/now'
import * as d3 from 'd3'

const interval = 1000 / 60

function HitSequence(hits, loop, duration) {
    duration = duration || 1000

    this.hits = _clone(hits)
    this.loop = loop
    this.duration = duration
    this.hitEnterCallback = null

    this.t = 0
    this.timer = null
    this.timeRangeMaps = []

    this.hitsEntered = this.hits.map(() => false)
}

// TODO    add a factory to play each hit with its own actual duration?

// TODO    add option to specify total duration instead?
/**
 * Creates a sequence that presents hits one at a time
 * @param hitObjects
 * @param loop
 * @param duration - duration of each hit
 */
HitSequence.sequential = function (hitObjects, loop, duration) {
    const durationTotal = hitObjects.length * duration
    const sequence = new HitSequence(hitObjects, loop, durationTotal)

    // calculate sequence.timeRangeMaps
    sequence.timeRangeMaps = hitObjects.map((hit, i) => {
        const t0 = i * duration
        const t1 = t0 + duration

        return d3.scaleLinear().domain([t0, t1]).range([0, 1]).clamp(true)
    })

    return sequence
}

/**
 * Creates a sequence that presents hits all at once
 * @param hitObjects
 * @param loop
 * @param duration - total duration
 */
HitSequence.simultaneous = function (hitObjects, loop, duration) {
    duration = duration || 5000

    const sequence = new HitSequence(hitObjects, loop, duration)

    // calculate timeRangeMaps
    sequence.timeRangeMaps = hitObjects.map(() =>
        d3.scaleLinear().domain([0, duration]).range([0, 1]).clamp(true)
    )

    return sequence
}

/**
 * Creates a sequence that presents hits overlapped in time
 * @param hitObjects
 * @param loop
 * @param duration - duration of each hit
 */
HitSequence.staggered = function (hitObjects, loop, duration) {
    duration = duration || 5000

    const sequence = new HitSequence(hitObjects, loop)
    const count = hitObjects.length
    const offsets = []

    _range(count).forEach((i) => {
        if (i === 0) {
            offsets.push(0)
        }
        const prevOffset = _last(offsets)
        const theta = (i / (count - 1)) * 2 * Math.PI
        const f = (Math.cos(theta) + 1) / 2
        const tDelta = f * duration

        offsets.push(prevOffset + tDelta)
    })

    // console.log('offsets: ' + offsets.map(function (d) {
    //         return d.toFixed(2)
    //     }).join(', '));

    // calculate sequence.timeRangeMaps
    sequence.timeRangeMaps = hitObjects.map((hit, i) => {
        const t0 = offsets[i]
        const t1 = t0 + duration

        return d3.scaleLinear().domain([t0, t1]).range([0, 1]).clamp(true)
    })

    return sequence
}

/**
 * Creates a sequence that presents hits overlapped in time
 * @param hitObjects
 * @param loop
 * @param duration - duration of each hit
 */
HitSequence.staggered2 = function (hitObjects, loop, duration) {
    duration = duration || 5000

    const sequence = new HitSequence(hitObjects, loop)
    const count = hitObjects.length
    const offsets = []

    _range(count).forEach((i) => {
        let tDelta
        const prevOffset = _last(offsets)

        switch (i) {
            case 0:
                offsets.push(0)
                return
            case 1:
                tDelta = duration
                break
            case 2:
                tDelta = 0.7 * duration
                break
            case 3:
                tDelta = 0.5 * duration
                break
            case 4:
                tDelta = 0.3 * duration
                break
            case 5:
                tDelta = 0.1 * duration
                break
            default:
                tDelta = 0.05 * duration
        }

        offsets.push(prevOffset + tDelta)
    })

    // console.log('offsets: ' + offsets.map(function (d) {
    //         return d.toFixed(2)
    //     }).join(', '));

    // calculate sequence.timeRangeMaps
    sequence.timeRangeMaps = hitObjects.map((hit, i) => {
        const t0 = offsets[i]
        const t1 = t0 + duration

        return d3.scaleLinear().domain([t0, t1]).range([0, 1]).clamp(true)
    })

    return sequence
}

HitSequence.prototype.reset = function () {
    this.t = 0

    this.hitsEntered = this.hits.map(() => false)

    return this
}

function maxTime(timeRangeMaps) {
    const outTimes = timeRangeMaps.map((trMap) => trMap.invert(1))

    return d3.max(outTimes)
}

HitSequence.prototype.play = function () {
    const that = this

    // check if already playing
    if (this.timer) {
        return undefined
    }

    // automatically calculate end time
    this.duration = maxTime(this.timeRangeMaps)

    const tStart = _now()

    this.timer = setInterval(() => {
        const t = _now() - tStart

        if (t <= that.duration) {
            that.t = t
            that.update()
        } else {
            clearInterval(that.timer)
            that.timer = null
            that.loop.stop()
            if (that.sequenceEndCallback) {
                that.sequenceEndCallback()
            }
        }
    }, interval)

    this.loop.start()

    return this
}

HitSequence.prototype.pause = function () {
    if (this.timer) {
        clearInterval(this.timer)
        this.loop.stop()
    }
    this.timer = null
    return this
}

HitSequence.prototype.start = function () {
    this.pause().reset().play()
    return this
}

HitSequence.prototype.onHitEnter = function (cb) {
    this.hitEnterCallback = cb
    return this
}

HitSequence.prototype.onSequenceEnd = function (cb) {
    this.sequenceEndCallback = cb
    return this
}

// for each hit, use its timescale to map tSequence to tHit (to value)
HitSequence.prototype.update = function () {
    // console.log('sequence - t: ' + this.t);

    const that = this

    this.hits.forEach((hit, i) => {
        const timescale = that.timeRangeMaps[i]
        const to = timescale ? timescale(that.t) : 1

        hit.show(0, to)

        if (to > 0 && !that.hitsEntered[i]) {
            // console.log('entering hit: ' + i);

            that.hitsEntered[i] = true

            if (that.hitEnterCallback) {
                that.hitEnterCallback(hit)
            }
        }
    })

    return this
}

export default HitSequence
